<template>
  <WdsModal :show="show" :title="title" lg @cancel="close">
    <WdsStack direction-column gap-md>
      <WdsStack direction-row align-center justify-space-between gap-md>
        <WdsText secondary>
          {{ description }}
        </WdsText>
        <WdsButtonGhost
          v-if="canSelectAll"
          secondary
          data-test="link-agency-select-all"
          @click="selectAll"
        >
          {{
            isSelectingAll
              ? $t('Selecting all agencies…')
              : $t('Select all :count agencies', { count: `${totalCount}` })
          }}
        </WdsButtonGhost>
      </WdsStack>
      <WdsAlert v-if="serverError" error data-test="link-agency-error">{{ serverError }}</WdsAlert>
      <WdsAlert v-if="removals.length > 0" warning icon data-test="link-agency-unlink-warning">
        {{
          $tc(
            'Saving will unlink :count staffing agency from :client | Saving will unlink :count staffing agencies from :client',
            removals.length,
            { client: clientLabel },
          )
        }}
        <template #description>
          {{ removalNames }}
          {{ $t('The links and their history are kept, and you can link them again later.') }}
        </template>
      </WdsAlert>
      <div class="tw-flex tw-flex-col">
        <!-- Tall screens: table fills what the modal chrome leaves; short screens: floor at ~5 rows and let the overlay scroll. -->
        <CompanyRecruitersBaseTable
          v-model:selected-rows="selectedRows"
          v-model:total-count="totalCount"
          v-model:select-all="selectingAll"
          multi-select
          max-height-adjustment="min(300px, calc(100vh - 606px))"
          :is-row-pre-selected="mode === 'manage' ? isRowPreSelected : undefined"
          :pre-selected-count="mode === 'manage' ? linkedAgenciesCount : 0"
        >
          <template #action="{ row, toggleSelection }">
            <WdsButton
              :primary="!isSelected(row)"
              :secondary="isSelected(row)"
              sm
              :data-test="`link-agency-toggle-${row.id}`"
              @click.stop="toggleSelection(row)"
            >
              {{ isSelected(row) ? $t('Deselect') : $t('Select') }}
            </WdsButton>
          </template>
        </CompanyRecruitersBaseTable>
      </div>
    </WdsStack>
    <template #footer>
      <WdsStack direction-row gap-sm justify-end>
        <WdsButton secondary @click="close">{{ $t('Cancel') }}</WdsButton>
        <WdsButton
          primary
          :disabled="!hasChanges || linking"
          data-test="link-agency-submit"
          @click="save"
        >
          <WdsSpinner v-if="linking" :size="16" indeterminate />
          <template v-else>
            {{ submitLabel }}
          </template>
        </WdsButton>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import type { ComputedRef } from 'vue'
import { computed, provide, ref, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  WdsAlert,
  WdsButton,
  WdsButtonGhost,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { graphql } from '@/gql'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import CompanyRecruitersBaseTable from '@/components/Contacts/CompanyRecruiter/CompanyRecruitersBaseTable.vue'
import { TableInModal } from '@/components/GraphqlDataTable/injection'
import { useAccountsStore } from '@/components/GraphqlDataTable/filters/accounts/accounts-store'
import { SUPPLIER_LINK_QUERIES } from '@/components/Clients/supplierLinkQueries'

type AgencyRow = {
  id: string
  recruiter?: { name?: string }
  endClients?: { data: Array<{ id: string; client: { id: string } }> }
}

const SET_LINKS = graphql(`
  mutation SetSupplierClientLinks($input: SetSupplierClientLinksInput!) {
    setSupplierClientLinks(input: $input) {
      id
      companyRecruiter {
        id
        endClients(first: 100) {
          data {
            id
            client {
              id
              name
            }
          }
          paginatorInfo {
            total
          }
        }
      }
    }
  }
`)

const LINKED_AGENCIES_COUNT = graphql(`
  query LinkStaffingAgencyLinkedCount($accounts: [ID!], $endClients: [ID!], $first: Int!) {
    companyRecruiters(accounts: $accounts, endClients: $endClients, first: $first) {
      paginatorInfo {
        total
      }
    }
  }
`)

const props = defineProps<{
  show: boolean
  mode: 'bulk' | 'manage'
  clients: Array<{ id: string; name: string }>
}>()

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void
  (e: 'linked'): void
}>()

provide(TableInModal, true)

const selectedRows = ref<Record<string, object>>({})
const initiallyLinkedRows = ref<Map<string, AgencyRow>>(new Map())

const totalCount = ref(0)
const selectingAll = ref(false)
const isSelectingAll = computed(() => selectingAll.value)

const canSelectAll = computed(
  () => totalCount.value > 0 && Object.keys(selectedRows.value).length < totalCount.value,
)

function selectAll() {
  selectingAll.value = true
}

function isSelected(row: AgencyRow): boolean {
  return row.id in selectedRows.value
}

const { selectedAccounts } = useAccountsStore()

const clientIds = computed(() => props.clients.map((client) => client.id))
const singleClientId = computed(() => clientIds.value[0] ?? null)

const description = computed(() => {
  if (props.mode === 'manage') {
    return $t(
      'Staffing agencies already linked to :client are ticked. Tick to link, untick to unlink.',
      { client: props.clients[0]?.name ?? '' },
    )
  }
  return props.clients.length === 1
    ? $t('Select the staffing agencies to link to :client.', { client: props.clients[0].name })
    : $t('Select the staffing agencies to link to the :count selected clients.', {
        count: `${props.clients.length}`,
      })
})

const { result: countResult } = useQuery(
  LINKED_AGENCIES_COUNT,
  () => ({
    ...(selectedAccounts.length === 0
      ? {}
      : { accounts: selectedAccounts.map((account) => account.id) }),
    endClients: [singleClientId.value ?? ''],
    first: 1,
  }),
  // Revalidate on open: cache normalisation cannot correct a paginator total.
  () => ({
    clientId: 'publicApi',
    enabled: props.show && props.mode === 'manage',
    fetchPolicy: 'cache-and-network',
  }),
)

const linkedAgenciesCount = computed(
  () => countResult.value?.companyRecruiters?.paginatorInfo.total ?? 0,
)

// "Link" while nothing is linked yet, "Manage" once there is something to manage.
const title = computed(() =>
  props.mode === 'manage' && linkedAgenciesCount.value > 0
    ? $t('Manage staffing agencies')
    : $t('Link staffing agencies'),
)

function isLinkedToClient(row: AgencyRow, clientId: string): boolean {
  return row.endClients?.data.some((link) => link.client.id === clientId) ?? false
}

// A predicate the table calls per row while rendering — it only answers, it never records.
function isRowPreSelected(row: object): boolean {
  if (singleClientId.value === null) return false

  return isLinkedToClient(row as AgencyRow, singleClientId.value)
}

// Anything selected and already linked is a removal candidate, however it was selected.
watch(
  selectedRows,
  (rows) => {
    if (props.mode !== 'manage' || singleClientId.value === null) return
    for (const row of Object.values(rows as Record<string, AgencyRow>)) {
      if (isLinkedToClient(row, singleClientId.value)) {
        initiallyLinkedRows.value.set(row.id, row)
      }
    }
  },
  // Immediate: pre-selected rows are in the selection before a lazy watch would run.
  { deep: true, immediate: true },
)

const additions = computed<AgencyRow[]>(() =>
  Object.values(selectedRows.value as Record<string, AgencyRow>).filter((row) =>
    clientIds.value.some((clientId) => !isLinkedToClient(row, clientId)),
  ),
)

const removals = computed<AgencyRow[]>(() => {
  if (props.mode !== 'manage') return []
  return [...initiallyLinkedRows.value.values()].filter((row) => !(row.id in selectedRows.value))
})

const hasChanges = computed(() => additions.value.length > 0 || removals.value.length > 0)

const clientLabel = computed(() => props.clients.map((client) => client.name).join(', '))

const removalNames = computed(() =>
  removals.value
    .map((row) => row.recruiter?.name)
    .filter(Boolean)
    .join(', '),
)

const submitLabel = computed(() => {
  if (removals.value.length > 0) {
    return $tc(
      'Save and unlink :count staffing agency | Save and unlink :count staffing agencies',
      removals.value.length,
    )
  }

  return props.mode === 'manage' ? $t('Save changes') : $t('Link agencies')
})

const {
  mutate,
  loading: linking,
  error: mutationError,
} = useMutation(SET_LINKS, { clientId: 'publicApi', refetchQueries: SUPPLIER_LINK_QUERIES })

const serverError: ComputedRef<string> = computed(
  () => mutationError.value?.graphQLErrors?.[0]?.message ?? '',
)

function close() {
  selectedRows.value = {}
  initiallyLinkedRows.value = new Map()
  emit('update:show', false)
}

async function save() {
  const apply = async (rows: AgencyRow[], clients: string[], linked: boolean) => {
    if (rows.length === 0) return

    await mutate(
      {
        input: {
          companyRecruiterIds: rows.map((row) => row.id),
          clientIds: clients,
          linked,
        },
      },
      { context: { skipGlobalErrorHandling: true } },
    ).catch(() => null)
  }

  await apply(additions.value, clientIds.value, true)
  if (mutationError.value) return

  const removalClient = singleClientId.value

  if (removalClient !== null) {
    await apply(removals.value, [removalClient], false)
    if (mutationError.value) return
  }

  close()
  emit('linked')
}
</script>
